// PatentBoardAdd.tsx
import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import { useState, useEffect, useRef } from "react";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import {
  requestCreatePatentDocuments,
  requestUpdatePatentDocuments,
  requestUploadFiles
} from "../../service/BoardService";
import { dialogStore } from "../../store/DialogStore";
import Patent from "../../model/board/Patent";
import TextField from "@mui/material/TextField";
import BTButton from "../../common/components/BTButton";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";
import FileIcon from "../../assets/icons/file_icon.svg";

const PatentBoardAdd = () => (
  <MainLayout>
    <PatentBoardAddBody />
  </MainLayout>
);

export default PatentBoardAdd;

const PatentBoardAddBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [englishTitle, setEnglishTitle] = useState("");
  const [chineseTitle, setChineseTitle] = useState("");
  const [representImageFile, setRepresentImageFile] = useState<File | null>(null);
  const [representImageFileUrl, setRepresentImageFileUrl] = useState<string | null>(null);
  const [patentType, setPatentType] = useState("");
  const [acquisitionDate, setAcquisitionDate] = useState("");
  const [isPublished, setIsPublished] = useState(true);

  const [patentTypeOptions, setPatentTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const options = location.state;
    if (options) {
        setPatentTypeOptions(options);
        setPatentType(options[0].value);
    }
  }, [location.state]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setRepresentImageFile(files[0]);
      setRepresentImageFileUrl(URL.createObjectURL(files[0]));
    }
  };

  const handleCancel = () => {
    dialogStore.showDialog({
      title: '확인',
      message: "정말 그만두시겠습니까?",
      onConfirm: () => navigate(-1),
      onCancel: () => {}
    });
  };

  const handleSave = async () => {
    try {
      if (!representImageFile) {
        dialogStore.showDialog({
          title: "확인",
          message: "대표 이미지를 첨부해주세요.",
          onConfirm: () => {}
        });
        return;
      }

      baseStore.showLoading();

      const now = new Date();
      const patent: Patent = {
        patentType,
        title,
        englishTitle,
        chineseTitle,
        representImage: "",
        acquisitionDate: acquisitionDate ? new Date(acquisitionDate) : null,
        isPublished,
        createdBy: authStore.user?.username ?? "",
        createdAt: now,
        updatedBy: authStore.user?.username ?? "",
        updatedAt: now
      };

      const docId = await requestCreatePatentDocuments(patent);
      patent.id = docId;

      const [fileUrl] = await requestUploadFiles("patent", docId, "attachments", [representImageFile]);
      patent.representImage = fileUrl;

      await requestUpdatePatentDocuments(patent);

      dialogStore.showDialog({
        title: "성공",
        message: "특허가 성공적으로 등록되었습니다.",
        onConfirm: () => navigate(-1),
        onClose: () => navigate(-1)
      });
    } catch (error) {
      dialogStore.showDialog({
        title: "오류",
        message: "특허 등록 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  // DateComponent 기준 높이(예: 36px)에 맞춰 TextField를 세팅
  const inputHeight = 36;

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>특허 추가</TitleLargeMedium>
      <BTBox height="24px" />
      <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

      <VerticalLayout height={"auto"} paddingBottom={36}>
        {/* 섹션: 특허 정보 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>특허 정보</TitleMediumBold>
        <BTBox height="16px" />

        {/* 1줄: 특허 유형 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D"
          }}
        >
          <b style={{ whiteSpace: "nowrap", width: '60px' }}>특허 유형</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <BTSelectBox
              width="100%"
              options={patentTypeOptions}
              value={patentType}
              onChange={(e) => setPatentType(e.target.value)}
            />
          </div>
        </div>

        <BTBox height="16px" />

        {/* 2줄: 특허명 → Divider → 취득일자 → Divider → 공개 여부 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D"
          }}
        >
          {/* 특허명 */}
          <b style={{ whiteSpace: "nowrap", width: '60px' }}>특허명</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <TextField
              label=""
              variant="outlined"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  height: `${inputHeight}px`,
                },
                '& .MuiOutlinedInput-input': {
                  padding: '0 12px',
                  height: `${inputHeight}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </div>

          {/* 특허명 뒤 Divider */}
          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          {/* 특허 취득 일자 */}
          <b style={{ whiteSpace: "nowrap", width: '80px' }}>특허 취득 일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={acquisitionDate} onChange={setAcquisitionDate} />
          </div>

          {/* 공개 여부 */}
          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />
          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
          <BTCheckBox title="" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
        </div>

        <BTBox height="16px" />

        {/* 2.5줄: 영문 특허명 → Divider → 중문 특허명 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D"
          }}
        >
          {/* 영문 특허명 */}
          <b style={{ whiteSpace: "nowrap", width: '60px' }}>영문 특허명</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <TextField
              label=""
              variant="outlined"
              fullWidth
              value={englishTitle}
              onChange={(e) => setEnglishTitle(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: `${inputHeight}px` },
                '& .MuiOutlinedInput-input': {
                  padding: '0 12px',
                  height: `${inputHeight}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </div>

          {/* Divider */}
          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          {/* 중문 특허명 */}
          <b style={{ whiteSpace: "nowrap", width: '80px' }}>중문 특허명</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <TextField
              label=""
              variant="outlined"
              fullWidth
              value={chineseTitle}
              onChange={(e) => setChineseTitle(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: `${inputHeight}px` },
                '& .MuiOutlinedInput-input': {
                  padding: '0 12px',
                  height: `${inputHeight}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </div>
        </div>

        <BTBox height="16px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

        {/* 섹션: 대표 이미지 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>특허 이미지</TitleMediumBold>
        <BTBox height="16px" />

        {/* ✅ 항상 표시되는 프리뷰/플레이스홀더 박스 */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
          }}
          aria-label="대표 이미지 선택"
          style={{
            width: 200,
            height: 300,
            border: representImageFileUrl ? "1px solid #ccc" : "2px dashed #C9CDD2",
            borderRadius: 6,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: representImageFileUrl ? "#fafafa" : "#FBFBFC",
            cursor: "pointer",
            userSelect: "none",
            transition: "border-color 0.15s ease",
          }}
        >
          {representImageFileUrl ? (
            <img
              src={representImageFileUrl}
              alt="첨부된 대표 이미지 미리보기"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
            />
          ) : (
            <div style={{ textAlign: "center", color: "#8A92A6", lineHeight: 1.4 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>이미지를 첨부해주세요</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>아래 <b>파일 선택</b> 버튼을 눌러 첨부</div>
              <div style={{ fontSize: 11, marginTop: 4, color: "#ADB5BD" }}>(권장: JPG/PNG, 5MB 이하)</div>
            </div>
          )}
        </div>

        <BTBox height="16px" />

        {/* 첨부파일(대표 이미지 선택) - 기존 그대로 */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
          <input
            ref={fileInputRef}
            id="patent-file-input"
            type="file"
            onChange={handleFileChange}
            accept="image/*"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
              border: 0
            }}
          />
          <BTButton
            width="auto"
            height="40px"
            color="#E7E7E7"
            textColor="#4E6173"
            onClick={() => fileInputRef.current?.click()}
            sx={{ px: 2, display: "inline-flex", alignItems: "center", gap: 1, fontSize: "14px" }}
            aria-label="파일 선택"
          >
            <img src={FileIcon} />
            <span>파일 선택</span>
          </BTButton>
        </div>

        <BTBox height="36px" />

        {/* 액션 버튼 */}
        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: "16px" }}>
          <BTButton color={theme.colors.bbTech} textColor="white" onClick={handleSave}>
            저장
          </BTButton>
          <BTButton onClick={handleCancel}>취소</BTButton>
        </div>
      </VerticalLayout>
    </VerticalLayout>
  );
});